// ============================================================================
// LIVE DRILL - scenario gauntlets played AT the table
// ============================================================================
// Every strategy-drill decision is staged as a real table spot: felt, seats,
// your cards in front of you, a ticking clock, and real action buttons.
// Scoring, pass thresholds, and reward claims are identical to the classic
// quiz runner, so the payout is exactly as hard to earn.
//
// Tutorial mode (?tutorial=1, bronze/silver tiers): the coach shows the
// winning play and why BEFORE you act, and you execute it to move on.
// Tutorial runs pay nothing - they exist so beginners can walk a set once,
// then try it for real.
// ============================================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Heading, Text, VStack, HStack, Badge, Progress,
} from '@chakra-ui/react';
import {
  ArrowLeft, CheckCircle, XCircle, MinusCircle, Coins, RotateCcw,
  GraduationCap, Target, BookOpen,
} from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import PokerTableSVG from '../components/PokerTableSVG';
import gameApiService from '../services/gameApiService';
import geoService from '../services/geoService';
import {
  getChallengeById, scoreRun, ChallengeStep, StepOption, TIER_META, getSkillLabel,
} from '../services/training/tournamentChallenges';
import { setGtoContext, clearGtoContext } from '../services/training/gtoContext';
import { handCodeFromCards } from '../data/gtoRanges';

const DECISION_SECONDS = 25;

// Fixed opponent pool - deterministic, no AI-generated feel.
const VILLAIN_NAMES = ['Viktor_R', 'DanaQQ', 'moose77', 'LinaPoker', 'Trapp3r'];
const POSITIONS_6MAX = ['UTG', 'MP', 'CO', 'BTN', 'SB', 'BB'];

// Parse "Middle stage · 60/120 (15)" style stage strings.
const parseStage = (stage: string): { sb: number; bb: number; ante: number } => {
  const blinds = stage.match(/([\d,]+)\s*\/\s*([\d,]+)/);
  const ante = stage.match(/\(([\d,]+)\)/);
  const num = (s?: string) => (s ? parseInt(s.replace(/,/g, ''), 10) : 0);
  const sb = num(blinds?.[1]) || 50;
  const bb = num(blinds?.[2]) || sb * 2;
  return { sb, bb, ante: num(ante?.[1]) };
};

// Best-effort read of the narrative so the table LOOKS like the spot:
// who raised (gets an action pill), who folded, plausible stacks.
const buildSeats = (step: ChallengeStep, stepIdx: number, currentUserId: string) => {
  const { sb, bb, ante } = parseStage(step.stage);
  const heroPos = step.position.toUpperCase();
  const ring = POSITIONS_6MAX.includes(heroPos)
    ? POSITIONS_6MAX
    : [...POSITIONS_6MAX.slice(0, 5), heroPos];
  const heroRingIdx = ring.indexOf(heroPos);

  const text = `${step.situation} ${step.actionTo}`;
  const foldsToYou = /folds? to you/i.test(text);
  const aggMatch = text.match(/\b(UTG|MP|HJ|CO|BTN|BUTTON|SB|BB)\b[^.!]*?\b(raises?|jams?|shoves?|opens?|3-?bets?|moves? all[- ]?in)\b/i);
  const aggPos = aggMatch ? aggMatch[1].toUpperCase().replace('BUTTON', 'BTN') : null;
  const aggIsJam = aggMatch ? /jam|shove|all[- ]?in/i.test(aggMatch[2]) : false;
  const bigStackMatch = text.match(/(\d+)\s*bb\s+chip\s*lead/i);

  let villain = 0;
  const players = ring.map((pos, i) => {
    if (i === heroRingIdx) {
      return {
        id: currentUserId,
        name: `You · ${heroPos}`,
        chips: Math.max(1, Math.round(step.heroBB * bb)),
        seat: i,
        folded: false,
        holeCards: step.hand,
      };
    }
    const name = `${VILLAIN_NAMES[villain % VILLAIN_NAMES.length]}`;
    villain += 1;
    const isAggressor = aggPos === pos;
    // Deterministic plausible stacks; the narrated chip leader gets theirs.
    let stackBB = 18 + ((i * 13 + stepIdx * 7) % 42);
    if (isAggressor && bigStackMatch) stackBB = parseInt(bigStackMatch[1], 10);
    const chips = Math.max(1, Math.round(stackBB * bb));
    return {
      id: `villain_${i}`,
      name: `${pos} · ${name}`,
      chips: isAggressor && aggIsJam ? 0 : chips,
      seat: i,
      folded: isAggressor ? false : foldsToYou || !!aggPos,
      allIn: isAggressor && aggIsJam,
      currentBet: isAggressor ? (aggIsJam ? chips : Math.round(bb * 2.2)) : 0,
      lastAction: isAggressor ? (aggIsJam ? 'ALL-IN' : 'RAISE') : undefined,
    };
  });

  const aggressorBet = players.reduce((s, p: any) => s + (p.currentBet || 0), 0);
  const pot = sb + bb + ante * ring.length + aggressorBet;
  return { players, heroIndex: heroRingIdx, pot, sb, bb, ante };
};

const optionStyle = (label: string): { bg: string; hover: string } => {
  if (/fold/i.test(label)) return { bg: '#dc2626', hover: '#b91c1c' };
  if (/all.?in|jam|shove/i.test(label)) return { bg: '#ea580c', hover: '#c2410c' };
  if (/call|limp|check/i.test(label)) return { bg: '#16a34a', hover: '#15803d' };
  if (/raise|bet|iso|3-?bet|min/i.test(label)) return { bg: '#2563eb', hover: '#1d4ed8' };
  return { bg: '#475569', hover: '#334155' };
};

interface Answer { stepId: string; action: string; evaluation: string; }

const LiveDrillPage: React.FC = () => {
  const { challengeId } = useParams<{ challengeId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tutorial = searchParams.get('tutorial') === '1';

  const challenge = useMemo(() => (challengeId ? getChallengeById(challengeId) : undefined), [challengeId]);

  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [picked, setPicked] = useState<StepOption | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [finished, setFinished] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [timeLeft, setTimeLeft] = useState(DECISION_SECONDS);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const pickedRef = useRef<StepOption | null>(null);

  const step = challenge?.steps[stepIdx];
  const seats = useMemo(
    () => (step ? buildSeats(step, stepIdx, 'live_drill_hero') : null),
    [step, stepIdx]
  );
  const bestOption = step?.options.find(o => o.evaluation === 'best') || step?.options[0];

  // Tell the floating Charts button which seat and stack this step is in
  useEffect(() => {
    if (!step) return;
    const position = step.position.toUpperCase().replace('BUTTON', 'BTN');
    const stackBB = step.heroBB;
    setGtoContext({
      position,
      stackBB,
      situation: stackBB <= 20 ? 'push' : 'open',
      hand: handCodeFromCards(step.hand),
      label: `${position} · ${Math.round(stackBB)} BB`,
    });
  }, [step]);
  useEffect(() => () => clearGtoContext(), []);

  const choose = (opt: StepOption, viaTimeout = false) => {
    if (pickedRef.current || !step) return;
    pickedRef.current = opt;
    setPicked(opt);
    setTimedOut(viaTimeout);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (!tutorial) {
      setAnswers(prev => [...prev, { stepId: step.id, action: opt.action, evaluation: opt.evaluation }]);
    }
  };

  // Decision clock - real runs only. Time out = the fold gets made for you,
  // exactly like a live table.
  useEffect(() => {
    if (tutorial || finished || !step) return;
    setTimeLeft(DECISION_SECONDS);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          const fold = step.options.find(o => /fold/i.test(o.action))
            || step.options.find(o => o.evaluation === 'bad')
            || step.options[0];
          choose(fold, true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, tutorial, finished]);

  const advance = () => {
    pickedRef.current = null;
    setPicked(null);
    setTimedOut(false);
    if (!challenge) return;
    if (stepIdx + 1 >= challenge.steps.length) {
      setFinished(true);
    } else {
      setStepIdx(i => i + 1);
    }
  };

  const restart = (asTutorial: boolean) => {
    pickedRef.current = null;
    setPicked(null);
    setTimedOut(false);
    setAnswers([]);
    setStepIdx(0);
    setFinished(false);
    setClaimResult(null);
    if (asTutorial !== tutorial) {
      navigate(`/training/tournament/${challengeId}/live${asTutorial ? '?tutorial=1' : ''}`, { replace: true });
    }
  };

  const claim = async () => {
    if (!challenge) return;
    setClaiming(true);
    // Same geo pattern as every training claim: PSS Coins in legal states,
    // PlayCoins fallback in restricted ones.
    const stateCode = geoService.getCachedResult()?.region;
    const r = await gameApiService.claimTournamentChallenge(challenge.id, answers, stateCode);
    setClaiming(false);
    if (r.success) {
      setClaimResult({
        ok: true,
        message: r.alreadyClaimed
          ? 'Already claimed - rewards pay once per challenge.'
          : r.geoRestricted
            ? `+${(r.playCoins ?? Math.round(challenge.rewardPkCash * 100)).toLocaleString()} PlayCoins credited (PSS Coins unavailable in your state).`
            : `+$${(r.rewardPkCash ?? challenge.rewardPkCash).toFixed(2)} PSS Coins credited (1x playthrough).`,
      });
    } else {
      setClaimResult({ ok: false, message: r.error || 'Claim failed' });
    }
  };

  if (!challenge || !step || !seats) {
    return (
      <Box minH="100vh" bg="gray.900" pt={10}>
        <Container maxW="3xl">
          <Text color="gray.300">Challenge not found.</Text>
          <PokerButton mt={4} size="sm" variant="secondary" onClick={() => navigate('/training/tournament')}>
            <ArrowLeft size={14} style={{ marginRight: 4 }} /> Back
          </PokerButton>
        </Container>
      </Box>
    );
  }

  const meta = TIER_META[challenge.tier];
  const { points, passed } = scoreRun(challenge, answers as any);

  // ── RESULTS ──────────────────────────────────────────────────────────────
  if (finished) {
    return (
      <Box minH="100vh" bg="gray.900" pb={16}>
        <Container maxW="3xl" pt={6}>
          <Heading size="md" color="white" mb={1}>{challenge.title}</Heading>
          {tutorial ? (
            <PokerCard>
              <VStack spacing={4} p={4}>
                <GraduationCap size={34} color={POKER_COLORS.cyan} />
                <Text color="white" fontWeight="bold" fontSize="lg">Walkthrough complete</Text>
                <Text color="gray.300" fontSize="sm" textAlign="center" maxW="440px">
                  You have seen how a winning player runs every spot in this set,
                  and why. Tutorial runs pay nothing - the reward is earned in a
                  real run, on the clock, with no coach.
                </Text>
                <PokerButton onClick={() => restart(false)}>
                  Try It For Real
                </PokerButton>
                <PokerButton variant="secondary" size="sm" onClick={() => navigate('/training/tournament')}>
                  Back to Tournament Training
                </PokerButton>
              </VStack>
            </PokerCard>
          ) : (
            <PokerCard>
              <VStack align="stretch" spacing={4} p={2}>
                <HStack justify="center" py={2}>
                  {passed ? (
                    <Badge colorScheme="green" fontSize="md" px={4} py={2} borderRadius="lg">
                      CHALLENGE PASSED
                    </Badge>
                  ) : (
                    <Badge colorScheme="red" fontSize="md" px={4} py={2} borderRadius="lg">
                      NOT PASSED - REVIEW & RETRY
                    </Badge>
                  )}
                </HStack>
                <Text color="gray.300" fontSize="sm" textAlign="center">
                  Score: <Text as="span" color={passed ? 'green.300' : 'red.300'} fontWeight="bold">
                    {points} / {challenge.steps.length}
                  </Text> (pass: {challenge.passThreshold}+)
                </Text>

                {/* Hand-by-hand recap */}
                <VStack align="stretch" spacing={2}>
                  {challenge.steps.map((s, i) => {
                    const a = answers[i];
                    const icon = a?.evaluation === 'best'
                      ? <CheckCircle size={14} color="#4ade80" />
                      : a?.evaluation === 'ok'
                        ? <MinusCircle size={14} color="#facc15" />
                        : <XCircle size={14} color="#f87171" />;
                    const best = s.options.find(o => o.evaluation === 'best');
                    return (
                      <Box key={s.id} bg="whiteAlpha.100" borderRadius="md" p={3}>
                        <HStack justify="space-between">
                          <HStack>{icon}
                            <Text color="white" fontSize="sm" fontWeight="600">
                              Hand {i + 1}: you chose {a?.action || 'nothing'}
                            </Text>
                          </HStack>
                          {a?.evaluation !== 'best' && (
                            <Text color="gray.400" fontSize="xs">Winning play: {best?.action}</Text>
                          )}
                        </HStack>
                        <Text color="gray.400" fontSize="xs" mt={1}>{s.keyLesson}</Text>
                      </Box>
                    );
                  })}
                </VStack>

                {passed && !claimResult && (
                  <PokerButton isLoading={claiming} onClick={claim}>
                    <Coins size={14} style={{ marginRight: 6 }} />
                    Claim ${challenge.rewardPkCash.toFixed(2)} PSS Coins
                  </PokerButton>
                )}
                {claimResult && (
                  <Text fontSize="sm" color={claimResult.ok ? 'green.300' : 'red.300'} textAlign="center">
                    {claimResult.message}
                  </Text>
                )}
                <HStack justify="center" spacing={3}>
                  <PokerButton variant="secondary" size="sm" onClick={() => restart(false)}>
                    <RotateCcw size={13} style={{ marginRight: 4 }} /> Run It Again
                  </PokerButton>
                  <PokerButton variant="secondary" size="sm" onClick={() => navigate('/training/tournament')}>
                    Back to Tournament Training
                  </PokerButton>
                </HStack>
              </VStack>
            </PokerCard>
          )}
        </Container>
      </Box>
    );
  }

  // ── AT THE TABLE ─────────────────────────────────────────────────────────
  return (
    <Box minH="100vh" bg={POKER_COLORS.darkBg} pb={10}>
      <Container maxW="5xl" pt={4}>
        {/* Header */}
        <HStack justify="space-between" mb={2}>
          <HStack>
            <PokerButton size="sm" variant="secondary" onClick={() => navigate('/training/tournament')}>
              <ArrowLeft size={14} style={{ marginRight: 4 }} /> Exit
            </PokerButton>
            <Text color="white" fontWeight="bold">{challenge.title}</Text>
            <Badge bg={`${meta.color}33`} color={meta.color} fontSize="9px">{meta.label}</Badge>
            {tutorial && (
              <Badge colorScheme="cyan" fontSize="9px" display="inline-flex" alignItems="center" gap="3px">
                <GraduationCap size={10} /> TUTORIAL - NO REWARD
              </Badge>
            )}
          </HStack>
          <HStack spacing={3}>
            <Text
              color="gray.400" fontSize="xs" cursor="pointer" textDecoration="underline"
              onClick={() => window.open('/training/glossary?category=tournament', '_blank')}
            >
              <BookOpen size={11} style={{ display: 'inline', marginRight: 3 }} />
              Glossary
            </Text>
            {!tutorial && (
              <Box px={3} py={1} borderRadius="full"
                bg={timeLeft <= 5 ? 'red.600' : timeLeft <= 10 ? 'orange.500' : 'green.600'}>
                <Text color="white" fontWeight="bold" fontSize="sm" fontFamily="mono">
                  0:{timeLeft.toString().padStart(2, '0')}
                </Text>
              </Box>
            )}
          </HStack>
        </HStack>

        <HStack justify="space-between" mb={1}>
          <Text color="gray.500" fontSize="xs">
            Hand {stepIdx + 1} of {challenge.steps.length} · {step.stage}
          </Text>
          <Text color={POKER_COLORS.cyan} fontSize="xs" fontWeight="semibold">
            {getSkillLabel(challenge.concept)}
          </Text>
        </HStack>
        <Progress value={((stepIdx) / challenge.steps.length) * 100} size="xs"
          colorScheme="cyan" borderRadius="full" mb={3} />

        {/* The table */}
        <Box position="relative" mb={3}>
          <PokerTableSVG
            players={seats.players as any}
            currentPlayerIndex={seats.heroIndex}
            pot={seats.pot}
            communityCards={[]}
            currentUserId="live_drill_hero"
            round="preflop"
            turnTimeLeft={tutorial ? undefined : timeLeft}
            turnDuration={tutorial ? undefined : DECISION_SECONDS}
          />
        </Box>

        {/* The spot */}
        <PokerCard>
          <VStack align="stretch" spacing={3} p={2}>
            <Box>
              <Text color="gray.300" fontSize="sm">{step.situation}</Text>
              <Text color={POKER_COLORS.cyan} fontSize="sm" fontWeight="bold" mt={1}>
                {step.actionTo}
              </Text>
            </Box>

            {/* Tutorial coach - the winning line BEFORE you act */}
            {tutorial && !picked && bestOption && (
              <Box bg="rgba(6,182,212,0.10)" border="1px solid rgba(6,182,212,0.4)" borderRadius="md" p={3}>
                <HStack mb={1}>
                  <GraduationCap size={14} color={POKER_COLORS.cyan} />
                  <Text fontSize="xs" color={POKER_COLORS.cyan} fontWeight="bold" letterSpacing="wide">
                    COACH: THE WINNING PLAY IS {bestOption.action.toUpperCase()}
                  </Text>
                </HStack>
                <Text color="gray.300" fontSize="sm">{bestOption.explanation}</Text>
                <Text color="gray.400" fontSize="xs" mt={2}>
                  Make that play to continue.
                </Text>
              </Box>
            )}

            {/* Feedback after acting */}
            {picked && (
              <Box
                bg={picked.evaluation === 'best' ? 'rgba(74,222,128,0.10)'
                  : picked.evaluation === 'ok' ? 'rgba(250,204,21,0.10)' : 'rgba(248,113,113,0.10)'}
                border={`1px solid ${picked.evaluation === 'best' ? 'rgba(74,222,128,0.45)'
                  : picked.evaluation === 'ok' ? 'rgba(250,204,21,0.45)' : 'rgba(248,113,113,0.45)'}`}
                borderRadius="md" p={3}
              >
                <HStack mb={1}>
                  {picked.evaluation === 'best' ? <CheckCircle size={15} color="#4ade80" />
                    : picked.evaluation === 'ok' ? <MinusCircle size={15} color="#facc15" />
                      : <XCircle size={15} color="#f87171" />}
                  <Text fontSize="sm" fontWeight="bold"
                    color={picked.evaluation === 'best' ? 'green.300' : picked.evaluation === 'ok' ? 'yellow.300' : 'red.300'}>
                    {timedOut ? 'CLOCK RAN OUT - THE FOLD WAS MADE FOR YOU'
                      : picked.evaluation === 'best' ? 'BEST PLAY'
                        : picked.evaluation === 'ok' ? 'PLAYABLE, NOT OPTIMAL' : 'COSTLY MISTAKE'}
                  </Text>
                </HStack>
                <Text color="gray.200" fontSize="sm">{picked.explanation}</Text>
                <Box mt={2} pt={2} borderTop="1px solid rgba(255,255,255,0.08)">
                  <Text color={POKER_COLORS.gold} fontSize="xs" fontWeight="bold" letterSpacing="wide">
                    KEY LESSON
                  </Text>
                  <Text color="gray.300" fontSize="sm">{step.keyLesson}</Text>
                </Box>
              </Box>
            )}

            {/* Action buttons */}
            {!picked ? (
              <HStack spacing={3} justify="center" flexWrap="wrap">
                {step.options.map(opt => {
                  const style = optionStyle(opt.action);
                  const locked = tutorial && bestOption && opt.action !== bestOption.action;
                  return (
                    <Box
                      key={opt.action}
                      as="button"
                      onClick={() => !locked && choose(opt)}
                      px={6} py={3} borderRadius="lg"
                      bg={locked ? '#1f2937' : style.bg}
                      opacity={locked ? 0.45 : 1}
                      cursor={locked ? 'not-allowed' : 'pointer'}
                      _hover={locked ? undefined : { bg: style.hover }}
                      border="1px solid rgba(255,255,255,0.15)"
                      minW="120px"
                    >
                      <Text color="white" fontWeight="bold" fontSize="sm" textTransform="uppercase">
                        {opt.action}
                      </Text>
                    </Box>
                  );
                })}
              </HStack>
            ) : (
              <HStack justify="center">
                <PokerButton onClick={advance}>
                  {stepIdx + 1 >= challenge.steps.length ? 'See Results' : 'Next Hand'}
                </PokerButton>
              </HStack>
            )}

            {!tutorial && !picked && (
              <HStack justify="center" spacing={1}>
                <Target size={11} color="#64748b" />
                <Text color="gray.500" fontSize="xs">
                  Pass with {challenge.passThreshold}+ points to earn ${challenge.rewardPkCash.toFixed(2)} PSS Coins.
                </Text>
              </HStack>
            )}
          </VStack>
        </PokerCard>
      </Container>
    </Box>
  );
};

export default LiveDrillPage;
